import {createContext, HTMLAttributes, useContext, useEffect, useState} from "react";

const viewportContext = createContext<ViewportContextData>({
    width: 0,
    height: 0
})

/**
 * Smart way of using JS to dynamically change components based on media queries instead of relying on
 *  CSS; normally, in CSS, we use Tailwind 'hidden' to toggle components based on media queries.
 *  However, sometimes, we need to use a completely different component structure where using JS
 *  gives more flexibility or control. One example is changing logo source image based on screen dimensions,
 *  which would require completely duplicating <img> component w/ CSS but is more efficient w/ JS only.
 *  CSS may be faster in some instances, though.
 *
 * TODO see if using [window.matchMedia] could be more uniform and more professional / etc.
 *
 * @source
 *  - https://blog.logrocket.com/developing-responsive-layouts-with-react-hooks/
 *  - https://developer.mozilla.org/en-US/docs/Web/API/Window/matchMedia
 *
 * @param children
 * @constructor
 */
export const ViewportProvider = ({children}: HTMLAttributes<any>) => {
    const [width, setWidth] = useState(window.innerWidth)
    const [height, setHeight] = useState(window.innerHeight)
    const handleWindowResize = () => {
        setWidth(window.innerWidth)
        setHeight(window.innerHeight)
    }

    useEffect(() => {
        window.addEventListener("resize", handleWindowResize)
        return () => window.removeEventListener("resize", handleWindowResize)
    }, [])

    return (
        <viewportContext.Provider value={{ width, height }}>
            {children}
        </viewportContext.Provider>
    )
}

/**
 * Just a helper method to shorten the accessing of the [viewportContext] object in other components
 */
export const useViewport = () => {
    const { width, height } = useContext(viewportContext)
    return { width, height }
}

type ViewportContextData = {
    width: number
    height: number
}
